/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQueryClient} from 'react-query';
import {getStaleTime} from '../utils/helpers';

// defining the usePaginationPrefetch hook props definition...
export type UsePaginationPrefetchOptions = {
  keys?: any[];
  key?: string;
  page: number;
  hasNext: boolean;
  hasPrevious?: boolean;
  previousPageKey?: string;
  resolver: (page: number) => void;
  staleTime?: number;
  retry?: boolean | number;
  enabled?: boolean;
  fetching?: boolean;
};

/**
 * The usePaginationPrefetch hook fetching the next page for the current page
 * (pre-fetching) and store in Query for referencing if the next page is exist in
 * pagination mode post search service.
 *
 * ```typescript
 *
 *  import {usePaginationPrefetch} from '@ion/query/src/hooks/';
 *
 *   usePaginationPrefetch({
 *     resolver: (page: number) => api({
 *       ...apiParams,
 *       pageId: page,
 *       pageSize : paging.pageSize
 *     }),
 *     hasNext: paging.hasNext,
 *     page: paging.page,
 *     keys
 *   });
 *
 * ```
 */
export const usePaginationPrefetch = ({
  key,
  keys,
  page,
  hasNext,
  resolver,
  retry,
  hasPrevious,
  previousPageKey,
  enabled = true,
  fetching = false,
  staleTime = getStaleTime(5, false, false) as number
}: UsePaginationPrefetchOptions): void => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (hasNext && enabled && !fetching) {
      const nextPage = page + 1;
      const nextKey  = key || [...keys.slice(0, -1), nextPage];
      queryClient.prefetchQuery(nextKey, () => (
        resolver(nextPage)
      ), {retry, staleTime});
    }
  }, [fetching, hasNext, page, key, queryClient, retry]);

  useEffect(() => {
    if (hasPrevious && enabled) {
      const previousPage = page - 1;
      const previousKey  = previousPageKey || [...keys.slice(0, -1), previousPage];
      queryClient.prefetchQuery(previousKey, () => (
        resolver(previousPage)
      ), {retry, staleTime});
    }
  }, [hasPrevious, page, previousPageKey, queryClient, retry]);
};

export default usePaginationPrefetch;
